import React, { useCallback, useState, useMemo } from 'react'
import { FileError } from 'react-dropzone'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useStateWithSetters from 'hooks/useStateWithSetters'
import DocumentDropzone from 'tripPlanner/components/Documents/DocumentDropzone'
import FileUploadError from 'tripPlanner/components/Documents/FileUploadError'
import FileUploadPreview from 'tripPlanner/components/Documents/FileUploadPreview'
import FileUploadSuccess from 'tripPlanner/components/Documents/FileUploadSuccess'
import MyDocuments from 'tripPlanner/components/Documents/MyDocuments'
import {
  useTripDocuments,
  useUploadDocument,
} from 'tripPlanner/hooks/api/documents'
import * as TripDocumentsMapper from 'tripPlanner/mappers/documents'
import { FullTrip } from 'tripPlanner/types/common'
import { TripDocumentWithItemInfo } from 'tripPlanner/types/documents'
import {
  flattenDocuments,
  getAttachedItemDisplayName,
} from 'tripPlanner/utils/documents'

interface Props {
  trip: FullTrip
  tripItemId?: string
}

function DocumentsModal({ trip, tripItemId }: Props) {
  const tripItem = trip.items.find((item) => item.id === tripItemId)

  const [currentView, setView] = useStateWithSetters([
    'selectUpload',
    'myDocuments',
    'uploading',
  ])

  const modalElement = useModalElementContext()
  const onClose = () => modalElement.resolve()

  const [dropzoneError, setDropzoneError] = useState<FileError | null>(null)

  const {
    mutate: uploadFile,
    isLoading: isUploading,
    isSuccess,
    isError: isUploadError,
    progress: uploadProgress,
    variables: uploadingFile,
    data: document,
    error: uploadError,
    retryUpload,
  } = useUploadDocument({
    tripId: trip.id,
    tripItemId,
    onMutate: () => setDropzoneError(null),
  })

  const isError = isUploadError || !!dropzoneError

  const onSelectFile = useCallback(
    (file: File) => {
      setDropzoneError(null)
      uploadFile(file)
      setView.uploading()
    },
    [setView, uploadFile],
  )

  const { data: documents } = useTripDocuments({ tripId: trip.id })

  const filteredMyDocuments = useMemo(() => {
    if (!documents) {
      return []
    }

    return flattenDocuments(trip, documents).filter(
      (document: TripDocumentWithItemInfo) =>
        !document.item || document.item.id === tripItemId,
    )
  }, [documents, trip, tripItemId])

  const onRejectFile = useCallback(
    (fileError: FileError) => {
      setDropzoneError(fileError)
      setView.uploading()
    },
    [setView],
  )

  let heading = tripItem
    ? `Attach document to ${getAttachedItemDisplayName(tripItem)}`
    : 'Upload document'
  if (isUploading) {
    heading = 'Uploading document'
  } else if (isSuccess) {
    heading = 'Upload successful'
  } else if (isError) {
    heading = 'Upload failed'
  }

  return (
    <ModalBase>
      <ModalHeader
        title={heading}
        onCloseButtonClick={onClose}
        onBackButtonClick={
          currentView === 'uploading' && isError
            ? setView.selectUpload
            : undefined
        }
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            {!!filteredMyDocuments.length &&
              (currentView === 'selectUpload' ||
                currentView === 'myDocuments') && (
                <TabButtonsGroup>
                  <TabButton
                    size="large"
                    isActive={currentView === 'selectUpload'}
                    onClick={setView.selectUpload}
                  >
                    Add new
                  </TabButton>
                  <TabButton
                    size="large"
                    isActive={currentView === 'myDocuments'}
                    onClick={setView.myDocuments}
                  >
                    My documents
                  </TabButton>
                </TabButtonsGroup>
              )}

            {/* Add new */}
            {currentView === 'selectUpload' && (
              <DocumentDropzone
                onSelectFile={onSelectFile}
                onRejectFile={onRejectFile}
                disabled={isUploading}
              />
            )}

            {currentView === 'uploading' && (
              <>
                {isUploading && (
                  <FileUploadPreview
                    filename={uploadingFile ? uploadingFile.name : 'File'}
                    progress={uploadProgress}
                  />
                )}
                {isSuccess && (
                  <FileUploadSuccess
                    document={TripDocumentsMapper.mapTripDocument(document)}
                  />
                )}
                {isError && (
                  <FileUploadError
                    uploadError={uploadError}
                    dropzoneError={dropzoneError}
                    onRequestRetry={retryUpload}
                  />
                )}
              </>
            )}

            {/* My documents */}
            {currentView === 'myDocuments' && (
              <MyDocuments
                tripId={trip.id}
                tripItemId={tripItemId}
                documents={filteredMyDocuments}
              />
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      {currentView === 'uploading' && (
        <>
          {isSuccess && (
            <ModalFooter
              primaryActionProps={{
                children: 'Done',
                onClick: onClose,
              }}
            />
          )}
          {isError && (
            <ModalFooter
              primaryActionProps={{
                children: 'Close',
                onClick: onClose,
              }}
            />
          )}
        </>
      )}
      {currentView === 'myDocuments' && (
        <ModalFooter
          primaryActionProps={{
            children: 'Done',
            onClick: onClose,
          }}
        />
      )}
    </ModalBase>
  )
}

export default DocumentsModal
